import { Dropdown, Input, InputProps } from '@components'
import { useBusinessContext } from '@context'
import { useDebouncedValue } from '@hooks'
import { fetchSimilarDocuments, IDocumentSimilarity } from '@query'
import { formatCurrency } from '@utils'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  DropdownWrapper,
  Header,
  HeaderDetail,
  HeaderIcon,
  Item,
  ItemColumn
} from './DocumentCopyDropdown.styled'
import styled from 'styled-components'

interface Props extends InputProps {
  label: string
  value: string
  onChange: (any) => void
  excludeIds?: number[]
  onCopy: (document: IDocumentSimilarity) => void
  disableSearch?: boolean
  importModelId?: number
}

export const DocumentCopyDropdown: React.FC<Props> = React.forwardRef(
  (
    {
      label,
      onChange: _onChange,
      onBlur,
      onCopy: _onCopy,
      excludeIds,
      importModelId,
      disableSearch = false,
      ...rest
    },
    ref
  ) => {
    const { slug } = useBusinessContext()
    const [isActive, setIsActive] = useState(false)
    const [focusIndex, setFocusIndex] = useState(-1)
    const { debouncedValue: debouncedSearchTerm, setValue: setDebouncedSearch } =
      useDebouncedValue<string>({
        initialValue: rest.value || '',
        delay: 300
      })
    const { t } = useTranslation()
    const anchorRef = React.useRef<HTMLDivElement | null>(null)

    const { data } = useQuery({
      queryKey: [slug, 'document_similarity', { debouncedSearchTerm, importModelId }],
      queryFn: () => fetchSimilarDocuments({ slug, search: debouncedSearchTerm, importModelId }),
      ...{
        enabled: isActive && debouncedSearchTerm.length >= 3 && !disableSearch,
        placeholderData: debouncedSearchTerm.length >= 3 ? keepPreviousData : undefined,
        // Add a stale time to avoid refetching too often same data
        staleTime: 1000 * 60
      }
    })
    const results = data
      ? data.filter(result => (excludeIds ? excludeIds.indexOf(result.id) == -1 : true))
      : []

    const onCopy = (document: IDocumentSimilarity) => {
      _onCopy(document)
    }

    const onChange = (value: string) => {
      setDebouncedSearch(value)
      _onChange(value)
    }

    const onKeyDown = e => {
      switch (e.key) {
        case 'ArrowDown': {
          setFocusIndex(Math.min(focusIndex + 1, results.length - 1))
          break
        }

        case 'ArrowUp': {
          setFocusIndex(Math.max(focusIndex - 1, 0))
          break
        }

        case 'Enter': {
          const result = results[focusIndex]
          if (result) {
            onChange(result.description)
            onCopy(result)
          }
          setFocusIndex(-1)
          setIsActive(false)
          e.stopPropagation()
          e.preventDefault()
          break
        }

        case 'Escape': {
          setFocusIndex(-1)
          setIsActive(false)
          break
        }
      }
    }

    return (
      <DropdownWrapper ref={anchorRef}>
        <Input
          data-test="document-form-description-input"
          ref={ref}
          autoComplete="off"
          label={label}
          onKeyDown={onKeyDown}
          onChange={e => {
            setIsActive(true)
            onChange(e.currentTarget.value)
          }}
          onBlur={e => {
            setIsActive(false)
            onBlur?.(e)
          }}
          {...rest}
        />

        {isActive && results.length > 0 && (
          <Dropdown anchorRef={anchorRef}>
            <Header>
              {t('document.copyHeader')}
              <HeaderIcon />
            </Header>
            <HeaderDetail>{t('document.copyHeaderDetail')}</HeaderDetail>

            <ItemWrapper>
              {results.map((result, i) => (
                <Item
                  key={result.id}
                  className={focusIndex === i && 'keyboard-focused'}
                  onTouchStart={() => {
                    onCopy(result)
                    onChange(result.description)
                  }}
                  onMouseDown={() => {
                    onCopy(result)
                    onChange(result.description)
                  }}
                >
                  <ItemColumn className="number">
                    <code>#{result.number}</code>
                  </ItemColumn>
                  <ItemColumn className="description">{result.description}</ItemColumn>
                  <ItemColumn className="balance">
                    <code>{formatCurrency(result.balance)}</code>
                  </ItemColumn>
                </Item>
              ))}
            </ItemWrapper>
          </Dropdown>
        )}
      </DropdownWrapper>
    )
  }
)

const ItemWrapper = styled.div`
  position: relative;
  min-height: 100px;
`
